import { useCallback, useEffect, useState } from "react";
import { ReviewState, TypedDocumentString } from "src/graphql/generated/graphql";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { handleError } from "../../../../utils/error";

// One approved review, trimmed to just what the report derives from: the fields the
// scheduler's evaluateCampaignResults reads, pros/cons for the feedback section, and
// submittedContents (the "<type>/<key>" destinations the review was submitted to). The
// funnel counts integrated e-shops by Campaign.reviewDeliveryCounts (isShipped), but custom
// "confirmSubmission" destinations never get isShipped — their "recenze vložena" signal is
// membership in submittedContents, so we still need it here.
export interface ReportReview {
    id?: string | null;
    state?: ReviewState | null;
    product?: { id?: string | null } | null;
    pros?: Array<string> | null;
    cons?: Array<string> | null;
    totalRating?: number | null;
    wouldRecommend?: boolean | null;
    wouldRecommendPercent?: number | null;
    submittedContents?: Array<string> | null;
    attributeRatings?: Array<{ id?: string | null; name?: string | null; rating?: number | null }> | null;
}

interface ReportReviewsResult {
    campaign?: {
        id?: string | null;
        reviews?: { nextToken?: string | null; items?: Array<ReportReview | null> | null } | null;
    } | null;
}

// A `type` (not `interface`) so it satisfies graphqlFetch's `Record<string, unknown>` bound.
type ReportReviewsVariables = {
    campaignId: string;
    state?: Array<ReviewState>;
    limit?: number;
    nextToken?: string | null;
};

// Lean, hand-authored query (not codegen'd) so we can page over ALL approved reviews
// without pulling `contents` — whose resolver does a per-review BatchGetItem and would
// make a campaign-wide fetch slow. productId is intentionally omitted: the resolver then
// returns every product's reviews for the campaign, which we group by product client-side.
const reportReviewsQuery = new TypedDocumentString<ReportReviewsResult, ReportReviewsVariables>(/* GraphQL */ `
    query ReportCampaignReviews($campaignId: String!, $state: [ReviewState!], $limit: Int, $nextToken: String) {
        campaign(campaignId: $campaignId) {
            id
            reviews(state: $state, limit: $limit, nextToken: $nextToken) {
                nextToken
                items {
                    id
                    state
                    product { id }
                    pros
                    cons
                    totalRating
                    wouldRecommend
                    wouldRecommendPercent
                    submittedContents
                    attributeRatings { id name rating }
                }
            }
        }
    }
`);

const PAGE_LIMIT = 200;
const MAX_PAGES = 30; // safety backstop (~6000 reviews) so a paging bug can't loop forever

// Fetches every approved review for the campaign (all products), paging to completion.
// Powers the client-side results recompute + e-shop funnel bars + feedback, so the Report
// tab works even on campaigns whose `campaign.results` was never evaluated by an admin.
export const useReportReviews = (campaign?: CampaignQueryDataType) => {
    const campaignId = campaign?.id ?? "";

    const [reviews, setReviews] = useState<ReportReview[]>([]);
    // Start loading when there's a campaign to fetch, so the report's global loading gate closes
    // on the first render instead of flashing empty sections before the effect fires.
    const [isLoadingReviews, setIsLoadingReviews] = useState(Boolean(campaignId));

    const fetchAll = useCallback(async (id: string) => {
        setIsLoadingReviews(true);
        try {
            const collected: ReportReview[] = [];
            let nextToken: string | null | undefined;
            let page = 0;
            do {
                const response = await graphqlFetch(reportReviewsQuery, {
                    campaignId: id,
                    // NB: the Campaign.reviews resolver hard-codes its filter to
                    // state == "approved" (or awaiting client approval) and ignores this arg,
                    // so only approved reviews ever come back. Rejected/censured counts must
                    // therefore come from the stored campaign.results (evaluated campaigns).
                    state: [ReviewState.Approved],
                    limit: PAGE_LIMIT,
                    nextToken,
                });
                for (const item of response.campaign?.reviews?.items || []) {
                    if (item) {
                        collected.push(item);
                    }
                }
                nextToken = response.campaign?.reviews?.nextToken;
                page += 1;
            } while (nextToken && page < MAX_PAGES);
            setReviews(collected);
        } catch (err) {
            handleError(err, { silent: true, context: { campaignId: id } });
            setReviews([]);
        } finally {
            setIsLoadingReviews(false);
        }
    }, []);

    useEffect(() => {
        if (!campaignId) {
            setReviews([]);
            return;
        }
        fetchAll(campaignId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    return { reviews, isLoadingReviews };
};
